<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's',
        [$style.mobileMenuActive]: showMobileMenu,
        [$style.isTreatwell]: isTreatwellUser,
        [$style.visibleScrollbar]: pageLayoutStore.isScrollbarVisible
      }
    ]"
    v-test="'the-header'"
  >
    <div
      v-if="$screen === 's'"
      :class="$style.logoMobile"
      @click="toggleMenu"
      v-test="'btn-mobile-nav'"
    >
      <div :class="$style.logoMobileInner">
        <div :class="$style.logo">
          <BaseLogo />
        </div>
      </div>
    </div>
    <div :class="$style.inner">
      <div :class="[$style.section, { [$style.mobileNav]: $screen === 's' }]">
        <div :class="$style.title">
          <div
            id="header-teleport"
            :class="$style.teleport"
          />
          <div :class="$style.titleText">{{ title }}</div>
          <div
            v-if="showCalendarNav"
            ref="calendarNavBtn"
            :class="$style.calendarNavBtn"
            v-test="'calendar-nav-settings-btn'"
          >
            <BaseIcon
              name="settings"
              :tooltip="
                activeNav === 'calendar'
                  ? null
                  : {
                      position: 'bottom',
                      text: $t('calendar.nav.tooltip')
                    }
              "
              @click="onNavClick('calendar', $event)"
            />
          </div>
          <transition name="pop">
            <div
              v-if="showCalendarOptionsIcon"
              :class="$style.calendarNavBtn"
              v-show="!previewLayoutActive"
              ref="calendarOptions"
              v-test="'calendar-options-icon'"
            >
              <BaseIcon
                name="options"
                :color="calendarDynamicPricingMode ? 'primary' : 'default'"
                @click="calendarOptionsActive = !calendarOptionsActive"
              />
            </div>
          </transition>
        </div>
        <div
          v-if="$screen === 's'"
          :class="$style.mobileNavRight"
        >
          <div :class="$style.mobileNavItem">
            <MobileContext />
          </div>
          <div :class="$style.mobileNavItem">
            <Search />
          </div>
          <Analyze
            v-if="user && (user.salonizedAdmin || user.treatwellAdmin)"
            :class="$style.mobileNavItem"
            @click="user.impersonated ? onNavClick('analyze', $event) : null"
          />
          <WavyOnboarding v-if="showWavyOnboarding" />
          <TrialTasks
            v-if="!showWavyOnboarding"
            :class="$style.trialTasksMobile"
          />
          <div
            :class="$style.mobileNavItem"
            @click="toggleMenu"
          >
            <BaseIcon
              :name="showMobileMenu ? 'close' : 'menu'"
              size="l"
              :color="showMobileMenu ? 'inverted' : undefined"
            />
          </div>
        </div>
      </div>
      <div
        v-if="$screen !== 's'"
        v-show="!previewLayoutActive"
        :class="$style.section"
      >
        <Search />
        <WavyOnboarding v-if="showWavyOnboarding" />
        <LocationSelect v-if="multiLocation" />
        <FranchiseSelect v-if="hasFeatureFlag('franchise-scope')" />
        <ThemeToggle />
        <Analyze v-if="user && (user.salonizedAdmin || user.treatwellAdmin)" />
        <BaseIcon
          v-if="hasFeatureFlag('trade-show-demo-account')"
          name="play-circle"
          @click="showDemoOnboarding = true"
          v-test="'onboardingIcon'"
          v-intercom="'onboarding-icon'"
        />
        <TrialTasks v-if="!showWavyOnboarding" />
        <div ref="user">
          <BaseAvatar
            :name="user.fullName"
            size="l"
            @click="onNavClick('user', $event)"
            v-intercom="'user-avatar'"
            v-test="'header-user-avatar'"
          />
        </div>
      </div>
      <CalendarSettingsNav
        v-show="showCalendarNav && activeNav === 'calendar'"
        :keepVisibleOn="$refs.calendarNavBtn"
        :positionOn="$refs.calendarNavBtn"
        @close="activeNav = ''"
        v-test="'calendar-settings-nav'"
      />
      <CalendarOptionsNav
        v-if="showCalendarOptionsIcon"
        v-show="calendarOptionsActive"
        :keepVisibleOn="$refs.calendarOptions"
        :positionOn="$refs.calendarOptions"
        @close="calendarOptionsActive = false"
        v-test="'calendar-options-nav'"
      />
      <BasePopoverNav
        v-show="activeNav === 'user'"
        :style="{
          top: '60px',
          right: '17px'
        }"
        arrow="topRight"
        :items="userItems"
        :keepVisibleOn="$refs.user || null"
        @close="activeNav = ''"
        @select="onSubNavItemSelect"
        v-test="'header-user-avatar-popover'"
      >
        <BaseText bold>{{ user.fullName }}</BaseText>
        <BaseText
          size="s"
          oneLine
          :style="{ marginTop: '4px' }"
        >
          {{ user.email }}
        </BaseText>
      </BasePopoverNav>
    </div>
    <DemoOnboarding
      v-if="showDemoOnboarding"
      @close="showDemoOnboarding = false"
    />
  </div>
</template>

<script lang="ts">
import { mapState, storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { logout } from '@/helpers/session';
import { useAdyenStore } from '@/stores/adyen';
import { useCalendarFiltersStore } from '@/stores/calendar-filters';
import { useCalendarPreviewStore } from '@/stores/calendar-preview';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { usePageLayoutStore } from '@/stores/page-layout';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';
import Analyze from './Analyze.vue';
import DemoOnboarding from './DemoOnboarding.vue';
import FranchiseSelect from './FranchiseSelect.vue';
import LocationSelect from './LocationSelect.vue';
import MobileContext from './MobileContext.vue';
import CalendarOptionsNav from './navs/CalendarOptionsNav.vue';
import CalendarSettingsNav from './navs/CalendarSettingsNav.vue';
import WavyOnboarding from './onboarding/wavy-onboarding/index.vue';
import Search from './search/index.vue';
import ThemeToggle from './ThemeToggle.vue';
import TrialTasks from './trial-tasks/index.vue';

export default defineComponent({
  name: 'TheHeader',
  components: {
    Search,
    Analyze,
    FranchiseSelect,
    CalendarSettingsNav,
    CalendarOptionsNav,
    LocationSelect,
    DemoOnboarding,
    TrialTasks,
    MobileContext,
    ThemeToggle,
    WavyOnboarding,
  },
  setup() {
    const pageLayoutStore = usePageLayoutStore();
    const { isInsideTestBucket, isTrial, isTreatwellUser } = useCompanyStore();
    const { previewLayoutActive } = storeToRefs(useCalendarPreviewStore());
    const { startAdyenVerificationSubscription } = useAdyenStore();

    startAdyenVerificationSubscription();

    const showWavyOnboarding = computed(() => {
      const { company } = useCompanyStore();
      return company?.fromWavy || false;
    });

    return {
      pageLayoutStore,
      isInsideTestBucket,
      isTrial,
      previewLayoutActive,
      unleash,
      isTreatwellUser,
      showWavyOnboarding,
    };
  },
  data() {
    return {
      activeNav: '',
      calendarOptionsActive: false,
      isSearchActive: false,
      showDemoOnboarding: false,
    };
  },
  watch: {
    $screen() {
      if (this.showMobileMenu && this.$screen !== 's') {
        this.pageLayoutStore.showMobileMenu = false;
      }
    },
    $route() {
      this.activeNav = '';
    },
  },
  computed: {
    ...mapState(useCompanyStore, [
      'company',
      'companySettings',
      'multiLocation',
    ]),
    ...mapState(useUserStore, ['user', 'hasFeatureFlag']),
    ...mapState(useLocationsStore, ['location']),
    ...mapState(usePageLayoutStore, ['showMobileMenu']),
    ...mapState(useCalendarFiltersStore, {
      calendarDynamicPricingMode: 'dynamicPricingMode',
    }),
    userItems() {
      const { isTreatwellUser, showReferralProgram } = useCompanyStore();

      const items = [
        {
          name: 'settings',
          icon: 'settings',
          label: this.$t('nav.settings'),
          routeName: 'admin',
        },
        {
          name: 'logout',
          icon: 'logout',
          label: this.$t('nav.logout'),
        },
      ];

      const account = {
        name: 'account',
        icon: 'person',
        label: this.$t('nav.edit_profile'),
        routeName: 'account',
      };

      const billing = {
        name: 'billing',
        icon: 'pricetag',
        label: this.$t('nav.billing'),
        routeName: 'admin-billing',
      };

      const referrals = {
        name: 'referrals',
        icon: 'gift',
        label: this.$t('nav.invite_colleagues'),
        routeName: 'admin-referrals',
      };

      if (this.hasFeatureFlag('module-billing')) {
        items.unshift(billing);
      }

      if (showReferralProgram) {
        items.unshift(referrals);
      }

      items.unshift(account);

      return items;
    },
    showCalendarNav() {
      return (
        this.$screen !== 's' &&
        !!this.$route.matched.find((route) => route.name === 'calendar')
      );
    },
    dynamicPricingEnabled() {
      const { hasFeatureFlag } = useUserStore();
      return (
        (hasFeatureFlag('dynamic-pricing') &&
          this.companySettings.bookings?.dynamicPricingEnabled) ||
        false
      );
    },
    showCalendarOptionsIcon() {
      return this.showCalendarNav && this.dynamicPricingEnabled;
    },
    title() {
      let title;
      const baseRoute = this.$route.matched.find(
        (route) => route.meta.moduleName,
      );
      const routeName = baseRoute?.meta.moduleName || '';
      switch (routeName) {
        case 'calendar':
          title = this.$t('nav.calendar');
          break;
        case 'customers':
          title = this.$t('nav.customers');
          break;
        case 'register':
          title = this.$t('nav.register');
          break;
        case 'products':
          title = this.$t('nav.products');
          break;
        case 'messages':
          title = this.$t('nav.messages');
          break;
        case 'feedback':
          title = this.$t('nav.feedback');
          break;
        case 'reports':
          title = this.$t('nav.reports');
          break;
        case 'marketing':
          title = this.$t('nav.marketing');
          break;
        case 'admin':
          title = this.$t('nav.settings');
          break;
        case 'integrations':
          title = this.$t('nav.integrations');
          break;
        case 'treatwell':
          title = this.$t('nav.treatwell');
          break;
        case 'treatwell-integration':
          title = this.$t('nav.treatwell-integration');
          break;
        case 'support':
          title = this.$t('nav.support_hub');
          break;
        case 'treatwell-landing':
          title = this.$t('nav.treatwell_landing');
          break;
        case 'online':
          title = this.$t('nav.online');
          break;
      }

      if (this.showMobileMenu) {
        title = '';
      }
      return title;
    },
  },
  methods: {
    toggleMenu() {
      this.pageLayoutStore.showMobileMenu = !this.showMobileMenu;
    },
    onNavClick(name, e) {
      e.stopPropagation();
      this.activeNav = this.activeNav === name ? '' : name;
    },
    onSubNavItemSelect(item) {
      switch (item.name) {
        case 'referrals':
          if (this.$route.name !== 'admin-referrals' && this.$gtm) {
            this.$gtm.trackEvent({
              event: 'referral-profile-click',
            });
          }
          break;
        case 'logout':
          logout();
          break;
      }

      this.activeNav = '';
      this.pageLayoutStore.showMobileMenu = false;
      this.activeNav = '';
    },
  },
  mounted() {
    this.pageLayoutStore.headerHeight = this.$el.offsetHeight;
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  position: relative;
  background-color: var(--surface-primary);

  &:not(.smallScreen) {
    z-index: 6;

    &:not(.visibleScrollbar) {
      padding-right: 7px; // Set padding right to compensate for the scrollbar and avoid 'jumping' of the content when the scrollbar is visible or hidden.
    }
  }

  &.smallScreen {
    position: fixed;
    width: 100%;
    padding-right: 0;
    height: $header-height;
    z-index: 6;

    &:not(.mobileMenuActive) {
      border-bottom: 1px solid var(--color-border);
    }
  }

  &.mobileMenuActive {
    background-color: var(--surface-nav);
  }

  @media print {
    display: none;
  }
}

.inner {
  position: relative;
  display: flex;
  gap: $spacing;
  justify-content: space-between;
  align-items: center;
  height: $header-height - 1px;

  .base:not(.smallScreen) & {
    padding: 0 $spacing;
  }
}

.section {
  display: flex;
  align-items: center;
  gap: $spacing;
}

.title {
  display: flex;
  align-items: center;
  font-size: 19px;
  font-weight: bold;
  color: var(--color-text);
  height: 100%;
  gap: $spacing * .5;

  .base.smallScreen & {
    font-size: 16px;
    padding-left: $spacing;
  }

  .base.mobileMenuActive & {
    color: white;
  }
}

.titleText {
  display: flex;
  align-items: center;
  height: 100%;
  overflow: hidden;
}

.searchIcon {
  &:global(.v-enter-active) {
    transition:
      transform 0.1s $easeOutBack 0.1s,
      opacity 0.1s linear 0.1s;
  }
  &:global(.v-enter-from),
  &:global(.v-leave-to) {
    opacity: 0;
    transform: scale(0.5);
  }
}

.calendarNavBtn {
  display: flex;
  align-items: center;
}

.mobileNav {
  position: relative;
  z-index: 2011;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  height: 50px;
  padding-left: 50px;
}

.mobileNavRight {
  display: flex;
  height: 100%;
}

.mobileNavItem {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  height: 50px;
}

.trialTasksMobile {
  margin-right: $spacing * 0.5;
}

.logo {
  width: 24px;
  height: 100%;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
}

.logoMobile {
  position: absolute;
  left: 0;
  top: 0;
  width: 50px;
  height: 50px;
  background: var(--surface-nav);
  cursor: pointer;
  // z-index must be higher than the one in nav-main for the mobile transition
  z-index: 2211;
}

.logoMobileInner {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.teleport {
  &:not(:empty) {
    & + .titleText {
      display: none;
    }
  }

  &:empty {
    display: none;
  }
}
</style>
